import {
  AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { TranslocoService } from '@ngneat/transloco';
import {
  buildChartOption, finiteOr, isStackedChart, PANEL_HEIGHT, PANEL_HEIGHT_COMPACT,
  shouldShowReducedNote, stackedChartHeight, zoomWindow, ZOOM_X_ID, ZOOM_Y_ID,
} from './chart-artifact.option';
import { ChartParseResult, parseChartArtifact } from './chart-artifact.parse';

/**
 * Renderer do artifact `chart` (contrato v1).
 *
 * Recebe o payload cru e decide entre desenhar e mostrar "gráfico não suportado". O contrato é
 * explícito: falha de renderer não pode interromper o texto da conversa nem a leitura do stream
 * até `done` — por isso nada aqui lança, e o estado de erro é parte da UI, não uma exceção.
 */
@Component({
  selector: 'app-chart-artifact',
  standalone: true,
  imports: [CommonModule],
  template: `
    <figure class="chart-artifact">
      <figcaption class="chart-head" *ngIf="title || fileName || parsed.ok">
        <i class="pi pi-chart-bar" aria-hidden="true"></i>
        <span class="chart-title">{{ title }}</span>
        <span class="chart-meta" *ngIf="fileName">{{ fileName }}</span>
        <div class="chart-toolbar" *ngIf="parsed.ok" role="group"
             [attr.aria-label]="t('chatbot.chart.zoomControls')">
          <button type="button" class="chart-tool" (click)="zoom('in')"
                  [attr.aria-label]="t('chatbot.chart.zoomIn')" [title]="t('chatbot.chart.zoomIn')">
            <i class="pi pi-search-plus" aria-hidden="true"></i>
          </button>
          <button type="button" class="chart-tool" (click)="zoom('out')"
                  [attr.aria-label]="t('chatbot.chart.zoomOut')" [title]="t('chatbot.chart.zoomOut')">
            <i class="pi pi-search-minus" aria-hidden="true"></i>
          </button>
          <button type="button" class="chart-tool" (click)="restore()"
                  [attr.aria-label]="t('chatbot.chart.restoreView')" [title]="t('chatbot.chart.restoreView')">
            <i class="pi pi-refresh" aria-hidden="true"></i>
            <span class="chart-tool-label">{{ t('chatbot.chart.restoreView') }}</span>
          </button>
        </div>
      </figcaption>

      <div #canvas class="chart-canvas" *ngIf="parsed.ok"></div>

      <p class="chart-note" *ngIf="showReducedNote">{{ t('chatbot.chart.reducedNote') }}</p>

      <div class="chart-unsupported" *ngIf="!parsed.ok">
        <i class="pi pi-exclamation-triangle"></i>
        <div>
          <p class="chart-unsupported-title">{{ t('chatbot.chart.unsupported') }}</p>
          <p class="chart-unsupported-reason">{{ reasonLabel }}</p>
        </div>
      </div>
    </figure>
  `,
  styles: [`
    .chart-artifact { margin: 8px 0 0; background: #0b0c0e; border: 1px solid #303030; }

    .chart-head { display: flex; align-items: center; gap: 8px; padding: 8px 12px;
                  border-bottom: 1px solid #303030; color: #d5d5d5; font-size: 12px; }
    .chart-head .pi { font-size: 13px; color: #9b9b9b; }
    .chart-title { flex: 1; min-width: 0; overflow-wrap: anywhere; }
    .chart-meta { color: #9b9b9b; white-space: nowrap; font-size: 11px; }

    .chart-toolbar { display: flex; align-items: center; gap: 4px; flex: 0 0 auto; }
    .chart-tool { display: inline-flex; align-items: center; justify-content: center;
                  min-width: 26px; height: 24px; padding: 0 6px; background: transparent;
                  border: 1px solid #3a3a3a; border-radius: 4px; color: #c8c8c8;
                  font: inherit; font-size: 11px; cursor: pointer; }
    .chart-tool:hover { color: #fff; border-color: #6a6a6a; }
    .chart-tool:focus-visible { outline: 2px solid var(--ftds-accent, #00ff00); outline-offset: 1px; }
    .chart-tool .pi { font-size: 12px; }
    .chart-tool-label { white-space: nowrap; margin-left: 4px; }

    .chart-canvas { width: 100%; height: 320px; }

    .chart-note { margin: 0; padding: 0 12px 8px; font-size: .72rem;
                  color: var(--ftds-text-dim, #888); }

    .chart-unsupported { display: flex; align-items: flex-start; gap: 10px; padding: 16px 14px;
                         color: var(--ftds-text-muted, #9a9ba0); }
    .chart-unsupported .pi { color: var(--ftds-warn, #f0b429); font-size: 15px; margin-top: 2px; }
    .chart-unsupported-title { margin: 0; color: var(--ftds-text-caption, #ccc); font-size: .88rem; }
    .chart-unsupported-reason { margin: 2px 0 0; font-size: .78rem; }

    /* A altura tambem depende da janela: um celular deitado passa de 640px de
       largura (a regra abaixo nao pegava) e mesmo assim so tem ~430px de
       altura, onde um grafico de 320px come a tela inteira. */
    @media (max-height: 620px) {
      .chart-canvas { height: min(260px, 45dvh); }
    }

    @media (max-width: 640px) {
      .chart-canvas { height: 260px; }
      .chart-meta { display: none; }
      /* Só o ícone em telas estreitas; o aria-label mantém o nome do controle. */
      .chart-tool-label { display: none; }
    }

    /* Celular deitado: vale o menor dos dois tetos. */
    @media (max-width: 640px) and (max-height: 620px) {
      .chart-canvas { height: min(260px, 45dvh); }
    }
  `],
})
export class ChartArtifactComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** Payload cru do evento SSE. A validação acontece aqui, não em quem chama. */
  @Input() payload: unknown;

  @ViewChild('canvas') private canvas?: ElementRef<HTMLDivElement>;

  parsed: ChartParseResult = { ok: false, reason: 'not_an_object' };

  private instance: echarts.ECharts | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private viewReady = false;
  /** Altura de faixa do último desenho; só redesenha quando ela muda de fato. */
  private drawnPanelHeight = 0;

  constructor(private transloco: TranslocoService) {}

  t = (key: string): string => this.transloco.translate(key);

  ngOnChanges(): void {
    this.parsed = parseChartArtifact(this.payload);
    // O canvas nasce sob *ngIf, então no primeiro OnChanges ele ainda não existe. Depois
    // disso, um payload novo (ou que passou de inválido a válido) precisa redesenhar.
    if (this.viewReady) {
      this.draw();
      if (!this.resizeObserver) this.observeResize();
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.draw();
    this.observeResize();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.instance?.dispose();
    this.instance = null;
  }

  /**
   * O card muda de largura sem a janela mudar — sidebar abrindo, conversa reabrindo,
   * legenda quebrando linha. Observar o container cobre esses casos e também o gráfico
   * que nasce com largura zero porque a bolha ainda não terminou de aparecer.
   */
  private observeResize(): void {
    const el = this.canvas?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => {
      // No empilhado a altura da faixa depende da largura da coluna; girar o
      // celular ou abrir a barra lateral pode cruzar o limite. A comparação com o
      // último valor é o que impede o observer de se realimentar.
      if (this.parsed.ok && isStackedChart(this.parsed.chart)
          && this.panelHeightPx() !== this.drawnPanelHeight) {
        this.draw();
        return;
      }
      this.instance?.resize();
    });
    this.resizeObserver.observe(el);
  }

  /** Faixa mais baixa em coluna estreita: com 92px, cinco canais não cabiam na tela do celular. */
  private panelHeightPx(): number {
    const w = this.canvas?.nativeElement.clientWidth ?? 0;
    return w > 0 && w <= 560 ? PANEL_HEIGHT_COMPACT : PANEL_HEIGHT;
  }

  private draw(): void {
    if (!this.parsed.ok) {
      this.instance?.dispose();
      this.instance = null;
      return;
    }
    const el = this.canvas?.nativeElement;
    if (!el) return;

    // A altura do empilhado sai do número de séries, então não cabe no CSS. O estilo
    // inline vence as regras de `.chart-canvas`; sem empilhamento ele sai de cena e a
    // folha volta a mandar.
    const panelHeight = this.panelHeightPx();
    if (isStackedChart(this.parsed.chart)) {
      el.style.height = `${stackedChartHeight(this.parsed.chart.series.length, panelHeight)}px`;
    } else {
      el.style.removeProperty('height');
    }
    this.drawnPanelHeight = panelHeight;

    this.instance ??= echarts.init(el, undefined, { renderer: 'canvas' });
    this.instance.setOption(buildChartOption(this.parsed.chart, panelHeight), true);
    this.instance.resize();
  }

  /**
   * Ampliar/reduzir pelos botões: para cada componente de zoom (X e Y), lê a janela atual — que um
   * gesto pode ter mudado — e aplica a próxima. Despacho por ID, não por índice.
   */
  zoom(direction: 'in' | 'out'): void {
    if (!this.instance) return;
    // No empilhado não existe componente de zoom em Y (cada faixa escala sozinha):
    // despachar num ID ausente não faria nada, mas pedir só o que está na opção
    // mantém o botão dizendo a verdade sobre o que ele mexe.
    for (const id of this.zoomIds()) {
      const next = zoomWindow(this.zoomWindowOf(id), direction);
      this.instance.dispatchAction({ type: 'dataZoom', dataZoomId: id, start: next.start, end: next.end });
    }
  }

  /** "Restaurar visão": volta ao enquadramento inicial. Só a visão muda; o artifact não. */
  restore(): void {
    this.instance?.dispatchAction({ type: 'restore' });
  }

  /** Componentes de zoom presentes na opção atual, entre os que o card comanda. */
  private zoomIds(): string[] {
    const option = this.instance?.getOption() as unknown as { dataZoom?: { id?: string }[] } | undefined;
    const present = new Set((option?.dataZoom ?? []).map((d) => d.id));
    return [ZOOM_X_ID, ZOOM_Y_ID].filter((id) => present.has(id));
  }

  /** Janela (em %) do componente de zoom `id`, saneada: valor não finito vira visão completa. */
  private zoomWindowOf(id: string): { start: number; end: number } {
    const option = this.instance?.getOption() as unknown as
      { dataZoom?: { id?: string; start?: unknown; end?: unknown }[] } | undefined;
    const dz = option?.dataZoom?.find((d) => d.id === id);
    return { start: finiteOr(dz?.start, 0), end: finiteOr(dz?.end, 100) };
  }

  get title(): string {
    return this.parsed.ok ? (this.parsed.chart.title ?? '') : '';
  }

  get fileName(): string | null {
    return this.parsed.ok ? this.parsed.chart.file_name : null;
  }

  /**
   * `points` e `source_points` são metadados técnicos: ficam no payload e no histórico, mas
   * fora da UI padrão — cabeçalho, legenda, tooltip e texto acessível. Quando há redução
   * conhecida, o que aparece é uma nota curta, uma vez por gráfico, sem alegar método de
   * amostragem. As coordenadas dos pontos seguem nos tooltips.
   */
  get showReducedNote(): boolean {
    return this.parsed.ok && shouldShowReducedNote(this.parsed.chart);
  }

  get reasonLabel(): string {
    return this.parsed.ok ? '' : this.t(`chatbot.chart.reason.${this.parsed.reason}`);
  }
}
